import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { formatGhs } from 'pages/FuelManagement/fuelOpsDemoData'
import { Flex, PageFooter } from 'styling/GlobalStyles'
import { OtherText } from 'styling/Typography'
import { formatDateForDisplay, handlePagination } from 'utils/helpers'
import { IGetAllInventoryAssignmentsSingleResponse } from 'types/api/inventoryItems.interface'
import { IInventoryIssueRow } from 'types/inventoryControl.interface'
import {
    IStoreCounterKindFilter,
    IStoreHistoryTabProps,
    IStoreLedgerRow,
} from 'types/inventoryCounter.interface'
import { ViewInventoryIssueModal } from '../Issues/ViewInventoryIssueModal'
import { ViewInventoryAssignmentModal } from '../Assignments/ViewInventoryAssignmentModal'
import { chargeToDisplay } from '../inventoryControlDemoData'
import { COUNTER_DEMO_LEDGER, COUNTER_KIND_META, counterLinesSummary } from '../inventoryCounterDemoData'
import { KindFilterBar, KindFilterPill, LedgerMetaText, LedgerValueText } from './StoreCounter.styled'

const KIND_FILTERS: IStoreCounterKindFilter[] = [
    { id: 'ALL', label: 'All' },
    { id: 'ISSUED', label: 'Issued' },
    { id: 'ASSIGNED', label: 'Assigned' },
    { id: 'RETURNED', label: 'Returned' },
]

const mapIssue = (issue: IInventoryIssueRow): IStoreLedgerRow => ({
    id: `ISS-${issue.id}`,
    kind: 'ISSUED',
    dateISO: issue.date,
    worker: issue.assignedForUser
        ? `${issue.assignedForUser.firstName} ${issue.assignedForUser.lastName}`
        : null,
    itemsSummary: counterLinesSummary(
        (issue.lines || []).map((line) => ({ quantity: line.quantity, name: line.inventoryItem?.name ?? 'Item' })),
    ),
    chargeLabel: issue.control ? chargeToDisplay(issue.control.chargeTo) : null,
    dueBackLabel: null,
    valueGhs: issue.control?.valueGhs ?? null,
    issueRef: issue,
})

const mapAssignment = (assignment: IGetAllInventoryAssignmentsSingleResponse): IStoreLedgerRow => ({
    id: `ASG-${assignment.id}`,
    kind: assignment.type == 2 ? 'RETURNED' : 'ASSIGNED',
    dateISO: assignment.createdAt,
    worker: null,
    itemsSummary: counterLinesSummary(
        (assignment.lines || []).map((line) => ({ quantity: line.quantity, name: line.inventoryItem?.name ?? 'Item' })),
    ),
    chargeLabel: null,
    dueBackLabel: null,
    valueGhs: null,
    assignmentRef: assignment,
})

export const HistoryTab = ({ localLedger, params, setParams }: IStoreHistoryTabProps) => {
    const { issues, totalPages } = useSelector((store: RootState) => store.inventoryIssues)
    const { assignments } = useSelector((store: RootState) => store.inventoryItems)
    const [kindFilter, setKindFilter] = useState<IStoreCounterKindFilter['id']>('ALL')
    const [issueModal, setIssueModal] = useState<{ open: boolean, value: IInventoryIssueRow }>({ open: false, value: null })
    const [assignmentModal, setAssignmentModal] = useState<{ open: boolean, value: IGetAllInventoryAssignmentsSingleResponse }>({ open: false, value: null })

    const hasRealRows = issues.length > 0 || (assignments?.data?.length || 0) > 0

    const rows = useMemo(() => {
        const realRows = [
            ...issues.map(mapIssue),
            ...(assignments?.data || []).map(mapAssignment),
        ]
        const demoRows = !hasRealRows && USE_OPS_MOCKS ? COUNTER_DEMO_LEDGER : []
        return [...localLedger, ...realRows, ...demoRows]
            .filter((row) => kindFilter === 'ALL' || row.kind === kindFilter)
            .sort((a, b) => new Date(b.dateISO).getTime() - new Date(a.dateISO).getTime())
    }, [issues, assignments, localLedger, kindFilter, hasRealRows])

    const columns: IHonestTableColumn<IStoreLedgerRow>[] = [
        {
            id: 'kind',
            header: 'KIND',
            cell: (row) => (
                <StatusPill status={COUNTER_KIND_META[row.kind].tone}>
                    {COUNTER_KIND_META[row.kind].label}
                </StatusPill>
            ),
        },
        { id: 'date', header: 'DATE', cell: (row) => formatDateForDisplay(row.dateISO, true) },
        {
            id: 'worker',
            header: 'WORKER',
            cell: (row) => (row.worker ? row.worker : <OtherText grey>—</OtherText>),
        },
        { id: 'items', header: 'ITEMS', minWidth: '14rem', cell: (row) => row.itemsSummary },
        {
            id: 'meta',
            header: 'CHARGED TO / DUE BACK',
            minWidth: '12rem',
            cell: (row) =>
                row.chargeLabel || row.dueBackLabel ? (
                    <LedgerMetaText>{row.chargeLabel || row.dueBackLabel}</LedgerMetaText>
                ) : (
                    <OtherText grey>—</OtherText>
                ),
        },
        {
            id: 'value',
            header: 'VALUE',
            align: 'right',
            cell: (row) =>
                row.valueGhs != null ? (
                    <LedgerValueText>{formatGhs(row.valueGhs)}</LedgerValueText>
                ) : (
                    <OtherText grey>—</OtherText>
                ),
        },
    ]

    const handleRowClick = (row: IStoreLedgerRow) => {
        if (row.issueRef) setIssueModal({ open: true, value: row.issueRef })
        else if (row.assignmentRef) setAssignmentModal({ open: true, value: row.assignmentRef })
    }

    return <>
        <Flex mb='16'>
            <KindFilterBar>
                {KIND_FILTERS.map((filter) => (
                    <KindFilterPill
                        key={filter.id}
                        type='button'
                        $active={kindFilter === filter.id}
                        onClick={() => setKindFilter(filter.id)}
                    >
                        {filter.label}
                    </KindFilterPill>
                ))}
            </KindFilterBar>
        </Flex>
        {!rows.length ? (
            <NoItems title='Movements' />
        ) : (
            <HonestTable columns={columns} data={rows} keyField='id' onClick={handleRowClick} />
        )}
        {issues.length > 0 && (
            <PageFooter>
                <Pagination
                    setPage={(page) => handlePagination(page, setParams, 30)}
                    pageCount={Math.ceil(totalPages)}
                    forcePage={(params.page ? params.page : 1) - 1}
                />
            </PageFooter>
        )}
        {issueModal.open && <ViewInventoryIssueModal value={issueModal.value} closeModal={() => setIssueModal({ value: null, open: false })} />}
        {assignmentModal.open && <ViewInventoryAssignmentModal value={assignmentModal.value} closeModal={() => setAssignmentModal({ value: null, open: false })} />}
    </>
}
